import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config/api';

export const Header = ({ navItems = [], logoUrl }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [openMobileAccordion, setOpenMobileAccordion] = useState(null);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState('EN');
  const [menuTimeout, setMenuTimeout] = useState(null);
  const [headerSettings, setHeaderSettings] = useState(null);
  const [brandSettings, setBrandSettings] = useState(null);

  const backendUrl = API_BASE;

  const languages = [
    { code: 'EN', label: 'English', enabled: true },
    { code: 'BM', label: 'Bahasa Malaysia', enabled: false },
    { code: 'CN', label: '中文', enabled: false },
  ];

  const defaultNavItems = [
    { 
      title: 'Universities',
      href: '/universities',
      columns: [
        { title: 'By Country', links: [{ label: 'USA', href: '#' }, { label: 'UK', href: '#' }, { label: 'Canada', href: '#' }, { label: 'Australia', href: '#' }, { label: 'Malaysia', href: '#' }] },
        { title: 'Featured University', links: [{ label: 'Harvard University', href: '#' }, { label: 'MIT', href: '#' }, { label: 'Stanford University', href: '#' }, { label: 'Oxford University', href: '#' }] },
        { title: '', links: [{ label: 'Yale University', href: '#' }, { label: 'Cambridge University', href: '#' }, { label: 'Princeton University', href: '#' }, { label: 'Columbia University', href: '#' }, { label: 'View All Universities →', href: '/universities' }] },
      ] 
    },
    { 
      title: 'Programs', 
      href: '/programs',
      columns: [
        { title: 'Level of Study', links: [{ label: 'Pre-University', href: '#' }, { label: 'Certification', href: '#' }, { label: 'Diploma', href: '#' }, { label: 'Bachelor Degree', href: '#' }, { label: "Master's / PhD", href: '#' }] },
        { title: 'Mode of Study', links: [{ label: 'Full-time', href: '#' }, { label: 'Part-Time', href: '#' }, { label: 'Online Distance Learning (ODL)', href: '#' }, { label: 'Flexible Mode', href: '#' }] },
        { title: 'Featured Programs', links: [{ label: 'MBA', href: '#' }, { label: 'Medicine', href: '#' }, { label: 'Foundation Studies', href: '#' }, { label: 'Information Technology', href: '#' }, { label: 'Accounting', href: '#' }, { label: 'Actuarial Science', href: '#' }, { label: 'Engineering', href: '#' }, { label: 'Law', href: '#' }] },
      ] 
    },
    { 
      title: 'Scholarships', 
      href: '/scholarships',
      columns: [
        { title: 'Type of Scholarship', links: [{ label: 'Merit-Based', href: '#' }, { label: 'Promotional Scholarship', href: '#' }, { label: 'Need-Based Scholarship', href: '#' }] },
        { title: 'Scholarship For', links: [{ label: 'Malaysian Students', href: '#' }, { label: 'International Students', href: '#' }] },
        { title: 'Featured Scholarship', links: [{ label: 'Global Excellence Award', href: '#' }, { label: 'ASEAN Scholarship', href: '#' }, { label: 'Merit Scholarship 2024', href: '#' }, { label: 'Future Leaders Grant', href: '#' }] },
      ] 
    },
    { 
      title: 'Guides', 
      href: '/blogs',
      columns: [
        { title: 'Resources', links: [{ label: 'Application Guide', href: '#' }, { label: 'Visa Guide', href: '#' }, { label: 'Finance Guide', href: '#' }, { label: 'Student Life', href: '#' }] },
        { title: 'Popular Articles', links: [{ label: 'How to Choose a University', href: '#' }, { label: 'Scholarship Application Tips', href: '#' }, { label: 'Study Abroad Checklist', href: '#' }, { label: 'Career Planning Guide', href: '#' }, { label: 'Student Visa FAQ', href: '#' }, { label: 'View All Guides →', href: '/blogs' }], wider: true },
      ] 
    },
  ];

  // Fetch header settings from CMS
  useEffect(() => {
    const fetchHeaderSettings = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/public/header-settings`);
        if (response.ok) {
          const data = await response.json();
          setHeaderSettings(data.settings);
        }
      } catch (err) {
        console.log('Using default header settings');
      }
    };
    fetchHeaderSettings();
  }, [backendUrl]);

  // Fetch brand settings for logo
  useEffect(() => {
    const fetchBrandSettings = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/public/brand-settings`);
        if (response.ok) {
          const data = await response.json();
          setBrandSettings(data.settings);
        }
      } catch (err) {
        console.log('Using default logo');
      }
    };
    fetchBrandSettings();
  }, [backendUrl]);

  // Build navigation from CMS settings or fallback to defaults
  const buildNavigation = () => {
    if (navItems && navItems.length > 0) return navItems;
    
    // If we have CMS settings, build navigation from CMS data
    const menuItems = headerSettings?.menu_items;
    if (menuItems && Array.isArray(menuItems) && menuItems.length > 0) {
      return menuItems.map((cmsItem) => {
        if (!cmsItem) return null;
        
        // If mega menu is enabled and has columns, use CMS mega menu data
        const megaColumns = cmsItem.mega_columns;
        if (cmsItem.mega_menu_enabled && megaColumns && Array.isArray(megaColumns) && megaColumns.length > 0) {
          const columns = megaColumns
            .filter(col => col && (col.title || (col.links && Array.isArray(col.links) && col.links.length > 0)))
            .map(col => ({
              title: col?.title || '',
              links: (col?.links && Array.isArray(col.links) ? col.links : []).map(link => ({
                label: link?.label || '',
                href: link?.url || '#'
              }))
            }));
          
          return {
            title: cmsItem.label || '',
            href: cmsItem.url || '#',
            columns: columns.length > 0 ? columns : null
          };
        }
        
        // Fallback: try to match with default nav items for mega menu
        const defaultItem = defaultNavItems.find(d => 
          d.href === cmsItem.url || (d.title && cmsItem.label && d.title.toLowerCase() === cmsItem.label.toLowerCase())
        );
        
        return {
          title: cmsItem.label || '',
          href: cmsItem.url || '#',
          columns: defaultItem?.columns || null
        };
      }).filter(Boolean); // Remove any null items
    }
    
    return defaultNavItems;
  };

  const navigation = buildNavigation();
  const defaultLogoUrl = '/placeholders/logo-default.svg';
  
  // Use logo from brand settings if available, otherwise use prop or default
  const headerLogo = brandSettings?.header_logo || {};
  const currentLogoUrl = headerLogo.image_url || logoUrl || defaultLogoUrl;
  const logoAlt = headerLogo.alt_text || 'Unisave';
  const logoLink = headerLogo.link_url || '/';
  const logoWidth = headerLogo.width || 'auto';
  const logoHeight = headerLogo.height || 40;

  // Get CTA settings
  const ctaText = headerSettings?.cta?.text || 'Consult Us';
  const ctaUrl = headerSettings?.cta?.url || '#';
  const ctaEnabled = headerSettings?.cta?.enabled !== false;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isLangOpen) {
      const close = () => setIsLangOpen(false);
      document.addEventListener('click', close);
      return () => document.removeEventListener('click', close);
    }
  }, [isLangOpen]);

  const handleMenuEnter = (index) => {
    if (menuTimeout) clearTimeout(menuTimeout);
    setActiveMegaMenu(index);
  };

  const handleMenuLeave = () => {
    const timeout = setTimeout(() => setActiveMegaMenu(null), 150);
    setMenuTimeout(timeout);
  };

  const handleDropdownEnter = () => {
    if (menuTimeout) clearTimeout(menuTimeout);
  };

  const handleDropdownLeave = () => {
    setActiveMegaMenu(null);
  };

  const getDropdownWidth = (index) => {
    if (index === 3) return '520px'; // Guides - 2 columns
    return '680px'; // Others - 3 columns
  };

  return (
    <>
      <header data-testid="header" className={`sticky top-0 z-50 transition-all duration-200 ${isScrolled ? 'shadow-md' : ''}`} style={{ backgroundColor: 'rgb(0, 34, 79)' }}>
        <div className="max-w-[1320px] mx-auto">
          <div className="flex items-center justify-between h-16 px-6 lg:px-10">
            <a href={logoLink} className="flex items-center" data-testid="header-logo">
              <img 
                src={currentLogoUrl} 
                alt={logoAlt} 
                style={{ 
                  width: `${logoWidth}px`, 
                  height: `${logoHeight}px`,
                  filter: headerLogo.image_url ? 'none' : 'brightness(0) invert(1)', 
                  background: 'transparent' 
                }} 
                className="object-contain" 
              />
            </a>

            <nav className="hidden md:flex items-center gap-1" data-testid="header-nav-desktop">
              {navigation.map((item, index) => (
                <div key={index} className="relative" onMouseEnter={() => handleMenuEnter(index)} onMouseLeave={handleMenuLeave}>
                  {item.href ? (
                    <a href={item.href} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeMegaMenu === index ? 'text-[#F2C900]' : 'text-white/90 hover:text-white'}`} data-testid={`nav-${item.title.toLowerCase()}`}>
                      {item.title}
                      <svg className={`w-4 h-4 transition-transform ${activeMegaMenu === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </a>
                  ) : (
                    <button className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeMegaMenu === index ? 'text-[#F2C900]' : 'text-white/90 hover:text-white'}`} data-testid={`nav-${item.title.toLowerCase()}`}>
                      {item.title}
                      <svg className={`w-4 h-4 transition-transform ${activeMegaMenu === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                  )}
                </div>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <div className="relative">
                <button onClick={(e) => { e.stopPropagation(); setIsLangOpen(!isLangOpen); }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="language-selector">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                  <span>{selectedLang}</span>
                </button>
                {isLangOpen && (
                  <div className="absolute top-full right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-[#E8E4DF] py-1 z-50">
                    {languages.map((lang) => (
                      <button key={lang.code} onClick={() => { if (lang.enabled) { setSelectedLang(lang.code); setIsLangOpen(false); }}} disabled={!lang.enabled} className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between ${lang.enabled ? (selectedLang === lang.code ? 'text-[#D4A853] bg-[#FDF6E9]' : 'text-[#00224F] hover:bg-[#F8F6F3]') : 'text-[#8B9AAB] cursor-not-allowed'}`}>
                        <span>{lang.label}</span>
                        {!lang.enabled && <span className="text-xs bg-[#F5F3F0] px-1.5 py-0.5 rounded">Soon</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {ctaEnabled && (
                <a href={ctaUrl} className="h-10 px-5 rounded-lg text-sm transition-all duration-200 hover:brightness-110 hover:scale-105 flex items-center" style={{ backgroundColor: 'rgb(242, 201, 0)', color: '#00224F', fontWeight: 600 }} data-testid="header-consult-us-cta">{ctaText}</a>
              )}
            </div>

            <button className="md:hidden p-2 rounded-lg text-white transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-testid="mobile-menu-button" aria-label="Toggle mobile menu" title="Toggle mobile menu">
              {isMobileMenuOpen ? <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> : <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>}
            </button>
          </div>
        </div>

        {activeMegaMenu !== null && navigation[activeMegaMenu]?.columns && Array.isArray(navigation[activeMegaMenu].columns) && (
          <div className="hidden md:block absolute top-full left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-xl border border-[#E8E4DF] mt-2" style={{ width: getDropdownWidth(activeMegaMenu) }} onMouseEnter={handleDropdownEnter} onMouseLeave={handleDropdownLeave}>
            <div className="p-6">
              <div className={`grid gap-6 ${activeMegaMenu === 3 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {(navigation[activeMegaMenu].columns || []).map((column, colIndex) => (
                  <div key={colIndex} className={column?.wider ? 'col-span-1' : ''}>
                    {column?.title && <h3 className="font-semibold text-[#00224F] mb-3 text-sm">{column.title}</h3>}
                    <ul className="space-y-2">
                      {(column?.links || []).map((link, linkIndex) => (
                        <li key={linkIndex}><a href={link?.href || '#'} className="text-sm text-[#5A6978] hover:text-[#D4A853] transition-colors block py-1">{link?.label || ''}</a></li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-white overflow-y-auto">
            <div className="px-4 py-3 border-b border-[#E8E4DF]">
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#5A6978]">Language:</span>
                <div className="flex gap-2">
                  {languages.map((lang) => (
                    <button key={lang.code} onClick={() => lang.enabled && setSelectedLang(lang.code)} disabled={!lang.enabled} className={`px-3 py-1 text-xs rounded font-medium ${selectedLang === lang.code && lang.enabled ? 'bg-[#00224F] text-white' : lang.enabled ? 'bg-[#F5F3F0] text-[#00224F]' : 'bg-[#F5F3F0] text-[#8B9AAB]'}`}>{lang.code}</button>
                  ))}
                </div>
              </div>
            </div>
            <nav className="px-4 py-4">
              {navigation.map((item, index) => (
                <div key={index} className="border-b border-[#E8E4DF]">
                  {item.href ? (
                    <a 
                      href={item.href}
                      className="w-full flex items-center justify-between py-4 text-[#00224F] font-medium"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.title}
                      <svg className="w-5 h-5 text-[#5A6978]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </a>
                  ) : (
                    <button className="w-full flex items-center justify-between py-4 text-[#00224F] font-medium" onClick={() => setOpenMobileAccordion(openMobileAccordion === index ? null : index)}>
                      {item.title}
                      <svg className={`w-5 h-5 text-[#5A6978] transition-transform ${openMobileAccordion === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                  )}
                  {openMobileAccordion === index && item?.columns && Array.isArray(item.columns) && (
                    <div className="pb-4 space-y-4 bg-[#F8F6F3] -mx-4 px-4 pt-2">
                      {(item.columns || []).map((column, colIndex) => (
                        <div key={colIndex}>
                          {column?.title && <h4 className="font-medium text-xs text-[#5A6978] uppercase mb-2">{column.title}</h4>}
                          <ul className="space-y-2 pl-2">
                            {(column?.links || []).map((link, linkIndex) => (
                              <li key={linkIndex}><a href={link?.href || '#'} className="text-sm text-[#00224F] hover:text-[#D4A853]">{link?.label || ''}</a></li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
            <div className="sticky bottom-0 p-4 bg-white border-t border-[#E8E4DF]">
              <button className="w-full h-12 rounded-lg text-base" style={{ backgroundColor: 'rgb(242, 201, 0)', color: '#00224F', fontWeight: 600 }}>Consult Us</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
